import { AreaChart } from '@mantine/charts';
import { Box, Group, Text } from '@mantine/core';
import type { ReactNode } from 'react';
import { tokens as T } from '../../theme/tokens';
import { tokens as fmtTokens } from '../../ui/format';
import type { ModelSeries, ThroughputSeries } from '../../stores/sources/types';

/* The two real charts.
 *
 * One trap is worth naming where it lives, because it is silent: on the chart
 * itself `strokeDasharray` is the GRID dash (Mantine defaults it to '5 5'),
 * while on a series it is the STROKE dash. Setting only the series value leaves
 * a dashed grid; setting only the chart value dashes nothing. Both appear
 * below, deliberately. */

export function LegendSwatch({
  color,
  dashed,
  label,
  value,
}: {
  color: string;
  dashed?: boolean;
  label: string;
  value: ReactNode;
}) {
  return (
    <Group gap={7} wrap="nowrap">
      <Box
        style={
          dashed
            ? { width: 14, borderTop: `3px dotted ${color}` }
            : { width: 14, height: 3, borderRadius: 999, background: color }
        }
      />
      <Text fz={12} c={T.ink3}>
        {label}
      </Text>
      <Text fz={12} fw={800}>
        {value}
      </Text>
    </Group>
  );
}

const AXIS_TICK = { fontSize: 11, fill: T.ink5 };

/** Five x labels across the range, whatever the bucket count — the design shows
 *  four dates and "today", not thirty overlapping ticks. */
function fiveTicks(points: { t: string }[]): string[] {
  const n = points.length;
  if (n <= 5) return points.map((p) => p.t);
  return [0, 1, 2, 3, 4].map((i) => points[Math.round((i * (n - 1)) / 4)]!.t);
}

export function ThroughputChart({
  data,
  height = 180,
}: {
  data: ThroughputSeries;
  height?: number;
}) {
  return (
    <AreaChart
      h={height}
      data={data.points}
      dataKey="t"
      type="default"
      series={[
        { name: 'tokensIn', label: 'Tokens in', color: T.coral },
        { name: 'tokensOut', label: 'Tokens out', color: T.teal },
        { name: 'cacheWrite', label: 'Cache write', color: T.violet, strokeDasharray: '2 5' },
        { name: 'cacheRead', label: 'Cache read', color: T.marigold, strokeDasharray: '2 5' },
      ]}
      curveType="linear"
      withDots={false}
      strokeWidth={2}
      gridAxis="y"
      gridColor={T.page}
      // Chart-level: the GRID dash. The design's grid is solid.
      strokeDasharray="0"
      withLegend={false}
      withXAxis
      withYAxis
      valueFormatter={fmtTokens}
      xAxisProps={{
        ticks: fiveTicks(data.points),
        interval: 0,
        tickLine: false,
        axisLine: { stroke: T.border },
        tick: AXIS_TICK,
      }}
      yAxisProps={{
        width: 44,
        tickCount: 3,
        tickLine: false,
        axisLine: false,
        tick: AXIS_TICK,
      }}
      // The two cache series are lines, not areas.
      areaProps={(s) => ({
        fillOpacity: s.name.startsWith('cache') ? 0 : 0.1,
        isAnimationActive: false,
      })}
    />
  );
}

export function ModelStackChart({
  data,
  height = 150,
}: {
  data: ModelSeries;
  height?: number;
}) {
  return (
    <AreaChart
      h={height}
      data={data.points}
      dataKey="t"
      type="stacked"
      series={data.models.map((m) => ({
        name: m.model,
        color: MODEL_COLOR[m.model] ?? T.faint,
      }))}
      curveType="linear"
      withDots={false}
      strokeWidth={1.5}
      fillOpacity={0.55}
      gridAxis="y"
      gridColor={T.page}
      strokeDasharray="0"
      withLegend={false}
      withXAxis
      withYAxis
      valueFormatter={fmtTokens}
      xAxisProps={{
        ticks: fiveTicks(data.points),
        interval: 0,
        tickLine: false,
        axisLine: { stroke: T.border },
        tick: AXIS_TICK,
      }}
      yAxisProps={{
        width: 44,
        tickCount: 3,
        tickLine: false,
        axisLine: false,
        tick: AXIS_TICK,
      }}
      areaProps={() => ({ isAnimationActive: false })}
    />
  );
}

export const MODEL_COLOR: Record<string, string> = {
  'claude-sonnet-4-6': T.coral,
  'gpt-5.2-codex': T.teal,
  'claude-opus-4-6': T.marigold,
  'kimi-k2.5': T.violet,
};
